import { css, styled, keyframes } from "@pigment-css/react";
import { clsx } from "clsx";
import type { Column, SortDirection } from "./types";
import { SortIcon } from "./ui/SortIcon";

const shimmer = keyframes({
  "0%": { backgroundPosition: "-200px 0" },
  "100%": { backgroundPosition: "200px 0" },
});

const TableContainer = styled("div")(({ theme }) => ({
  width: "100%",
  backgroundColor: theme.colors.neutral[0],
  borderRadius: theme.managementView.radius[16],
  overflow: "hidden",
}));

const Table = styled("table")(({ theme }) => ({
  width: "100%",
  borderCollapse: "collapse",
  fontFamily: theme.managementView.font.family,
}));

const TableHeader = styled("thead")(({ theme }) => ({
  borderBottom: `1px solid ${theme.managementView.colors.neutral[8]}`,
}));

const TableHeaderRow = styled("tr")({});

const TableHeaderCell = styled("th")(({ theme }) => ({
  padding: `${theme.managementView.spacing[16]} ${theme.managementView.spacing[32]}`,
  textAlign: "left",
  fontFamily: theme.managementView.font.family,
  fontSize: theme.managementView.font.size[15],
  fontWeight: theme.managementView.font.weight[500],
  letterSpacing: theme.managementView.font.letterSpacing.tight,
  color: theme.managementView.colors.neutral[56],
  whiteSpace: "nowrap",
}));

const sortableHeaderCell = css(({ theme }) => ({
  cursor: "pointer",
  "&:hover": {
    color: theme.managementView.colors.neutral[90],
  },
}));

const TableBody = styled("tbody")({});

const TableRow = styled("tr")(({ theme }) => ({
  borderBottom: `1px solid ${theme.managementView.colors.neutral[8]}`,
  "&:last-child": {
    borderBottom: "none",
  },
}));

const TableCell = styled("td")(({ theme }) => ({
  padding: `${theme.managementView.spacing[16]} ${theme.managementView.spacing[32]}`,
}));

const SkeletonElement = styled("div")(({ theme }) => ({
  borderRadius: theme.managementView.radius[10],
  background: `linear-gradient(90deg, ${theme.colors.neutral[200]} 0%, ${theme.colors.neutral[100]} 50%, ${theme.colors.neutral[200]} 100%)`,
  backgroundSize: "400px 100%",
  animation: `${shimmer} 1.5s ease-in-out infinite`,
}));

const CellSkeleton = styled(SkeletonElement)({
  height: "17px",
});

interface ManagementDataTableSkeletonProps {
  columns: Column[];
  rowCount?: number;
  sortColumn?: string | null;
  sortDirection?: SortDirection;
}

export const ManagementDataTableSkeleton = ({
  columns,
  rowCount = 5,
  sortColumn = null,
  sortDirection = null,
}: ManagementDataTableSkeletonProps) => {
  const columnWidths = ["120px", "100px", "100px", "60px", "60px"];

  return (
    <TableContainer>
      <Table>
        <TableHeader>
          <TableHeaderRow>
            {columns.map((column) => (
              <TableHeaderCell key={column.key} className={clsx(column.sortable && sortableHeaderCell)}>
                {column.label}
                {column.sortable && <SortIcon direction={sortDirection} isActive={sortColumn === column.key} />}
              </TableHeaderCell>
            ))}
          </TableHeaderRow>
        </TableHeader>
        <TableBody>
          {Array.from({ length: rowCount }).map((_, rowIndex) => (
            <TableRow key={rowIndex}>
              {columns.map((column, colIndex) => (
                <TableCell key={column.key}>
                  <CellSkeleton style={{ width: columnWidths[colIndex] || "80px" }} />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
